import React, { useState } from 'react';
import * as PropTypes from 'prop-types';
import cx from 'classnames';
import Caption from '../Caption';
import styles from './Input.module.scss';
import {
    InformationIcon,
    CloseCircleIcon,
    CloseIcon,
    CheckIcon,
    EyeIcon,
    EyeClosedIcon,
    SearchIcon,
    SpinnerIcon,
} from 'icons/solid';

/**
 * Inspired on notiz.dev blog post:
 * https://notiz.dev/blog/floating-form-field-with-tailwindcss#building-a-form-1
 */

/**
 * InputWrapper is also used by InputPhone
 */
export const InputWrapper = ({
    fieldName,
    className,
    wrapperClassName,
    customBorderRadius,
    style,
    outlined,
    type,
    label,
    required,
    disabled,
    icon,
    onIconClick,
    helpText,
    helpVariant,
    children,
    showValue,
}) => {
    const [hideInputValue, setHideInputValue] = useState(type === 'password' && !showValue);
    const toogleInputVisibility = () => setHideInputValue(!hideInputValue);

    return (
        <div className={className}>
            <div
                className={cx(
                    styles.inputWrapper,
                    wrapperClassName,
                    'relative w-full bg-ui-white',
                    'z-2',
                    {
                        'rounded-sm border': outlined && !customBorderRadius,
                        'border-b': !outlined,

                        'border-ui-black': !disabled,
                        'border-ui-gray-500': disabled,

                        'border-none': icon === 'applied',
                    }
                )}
                style={style}
            >
                {/*Children can be: input, textarea, phone input, etc,*/}
                {children({
                    hideInputValue: hideInputValue,
                })}

                {label && (
                    <label
                        htmlFor={fieldName}
                        className={cx(
                            'absolute top-0 left-0 origin-0 rounded-sm bg-ui-white px-2 duration-300',
                            'w-full overflow-hidden overflow-ellipsis font-body font-body-normal lg:truncate',
                            'flex h-full items-center py-0',
                            '-z-1',
                            {
                                'text-ui-black':
                                    !disabled && !(helpText && helpVariant === 'error'),
                                'text-ui-gray-500':
                                    disabled && !(helpText && helpVariant === 'error'),
                                'text-ui-error': helpText && helpVariant === 'error',
                                hidden: icon === 'applied',
                            }
                        )}
                    >
                        {label}
                        {required && <sup className='ml-0.5'>*</sup>}
                    </label>
                )}

                <InputIcon
                    icon={
                        type === 'password' && !hideInputValue
                            ? 'show_password'
                            : type === 'password' && hideInputValue
                            ? 'hide_password'
                            : icon
                    }
                    onIconClick={type === 'password' ? toogleInputVisibility : onIconClick}
                />
            </div>

            {helpText && (
                <Caption
                    className='mt-1.5'
                    classNameImg='w-5 h-5'
                    icon={
                        helpVariant === 'error' ? (
                            <CloseCircleIcon className='h-5 w-5 text-ui-error' />
                        ) : helpVariant === 'success' ? (
                            <CheckIcon className='mr-1 h-5 h-4 w-5 w-4 rounded-full bg-ui-success fill-white stroke-white stroke-2 p-1' />
                        ) : (
                            <InformationIcon className='h-5 w-5 text-brand-tertiary-700' />
                        )
                    }
                >
                    {helpText}
                </Caption>
            )}
        </div>
    );
};

const InputIcon = ({ icon, onIconClick }) => {
    const handleClickOnIcon = () => {
        if (onIconClick && icon !== 'loading') {
            onIconClick();
        }
    };

    const Icon = ({ iconClassName }) => {
        switch (icon) {
            case 'loading':
                return <SpinnerIcon className='mr-1 h-5 w-5 ' />;
            case 'show_password':
                return <EyeIcon className='mr-1 h-5 w-5 ' />;
            case 'hide_password':
                return <EyeClosedIcon className='mr-1 h-5 w-5 text-ui-gray-700' />;
            case 'success':
                return (
                    <CheckIcon className='mr-1 h-5 h-4 w-5 w-4 rounded-full bg-ui-success fill-white stroke-white stroke-2 p-1' />
                );
            case 'applied':
                return (
                    <CheckIcon className='mr-1 h-5 h-4 w-5 w-4 rounded-full border border-ui-success bg-ui-white fill-ui-success stroke-ui-success stroke-2 p-1' />
                );
            case 'clear':
                return <CloseIcon className='mr-1 h-5 w-5 ' />;
            case 'search':
                return <SearchIcon className='w-6' />;
            default:
                return <></>;
        }
    };

    if (!icon || icon === '') return <></>;

    return (
        <button
            className={cx(
                'absolute inset-y-0 flex items-center',
                icon === 'search' ? 'left-0 pl-2' : 'right-0 pr-2'
            )}
            type='button'
            onClick={handleClickOnIcon}
        >
            <Icon iconClassName='flex' />
        </button>
    );
};

const Input = React.forwardRef(
    (
        {
            id,
            large,
            placeholder,
            defaultValue,
            value,
            onChange,
            onChangeWithEvent,
            disabled,
            autoFocus,
            inputProps,
            textareaProps,
            autoComplete,
            onKeyUp,
            inputClassName,
            customBorderRadius,
            ...props
        },
        ref
    ) => {
        const { fieldName, type, required, icon, label } = props;

        const handleOnChange = (event) => {
            if (typeof onChange !== 'function') return;

            if (onChangeWithEvent) onChange(event);
            else {
                let value = event.target.value;
                onChange(value, event);
            }
        };

        const commonInputProps = {
            id: id,
            ref: ref,
            name: fieldName,
            label: label,
            className: cx(
                inputClassName,
                customBorderRadius,
                'block w-full p-2 border-0 appearance-none',
                'text-normal font-body font-body-normal',
                'focus:border-0 focus:ring-0 max-h-full',
                {
                    'bg-transparent': placeholder && !icon,
                    'placeholder-transparent focus:placeholder-ui-gray-500': label,
                    'h-11 sm:h-10': !large,
                    'text-ui-black': !disabled,
                    // opacity-100 & gray-900 required for iOS, iPadOS & macOS
                    'opacity-100 text-ui-gray-900 cursor-not-allowed': disabled,
                    'pr-9': icon && icon !== 'search',
                    'bg-ui-success text-white': icon === 'applied',
                    'rounded-sm': !customBorderRadius,
                }
            ),
            placeholder: placeholder,
            disabled: disabled,
            autoFocus: autoFocus,
            required: required,
            defaultValue: defaultValue,
            value: value,
            onChange: handleOnChange,
            onKeyUp: onKeyUp,
            autoComplete: autoComplete,
        };

        return (
            <InputWrapper {...props} disabled={disabled}>
                {({ hideInputValue }) => (
                    <>
                        {large ? (
                            <textarea {...commonInputProps} {...textareaProps} />
                        ) : (
                            <input
                                {...commonInputProps}
                                {...inputProps}
                                type={type === 'password' && !hideInputValue ? 'text' : type}
                            />
                        )}
                    </>
                )}
            </InputWrapper>
        );
    }
);

Input.defaultProps = {
    large: false,
    outlined: true,
    placeholder: ' ', // Default value (whitespace) for placeholder is required
    required: false,
    disabled: false,
    autoFocus: false,
    onChangeWithEvent: false,
    icon: null,
    helpText: null,
    helpVariant: 'error',
    showValue: false,
};

Input.propTypes = {
    id: PropTypes.string,
    fieldName: PropTypes.string,
    className: PropTypes.string,
    wrapperClassName: PropTypes.string,
    inputClassName: PropTypes.string,
    style: PropTypes.object,
    large: PropTypes.bool,
    outlined: PropTypes.bool,
    type: PropTypes.oneOf(['text', 'number', 'password', 'date']),
    label: PropTypes.string,
    placeholder: PropTypes.string,
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    autoFocus: PropTypes.bool,
    onChange: PropTypes.func,
    onChangeWithEvent: PropTypes.bool,
    icon: PropTypes.oneOf(['loading', 'clear', 'success', 'search', 'applied']),
    onIconClick: PropTypes.func,
    helpText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    helpVariant: PropTypes.oneOf(['warning', 'error', 'success']),
    inputProps: PropTypes.object,
    textareaProps: PropTypes.object,
    showValue: PropTypes.bool,
};

export default Input;
